import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

export default function HeroSection() {
  const { t, i18n } = useTranslation();
  const navigate = useHistory();
  const history = useHistory();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  const languageMap = {
    marathi: "मराठी",
    hindi: "हिंदी",
    english: "English",
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <section 
      className="relative h-[80vh] flex flex-col overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 25%, #8e44ad 75%, #9b59b6 100%)"
      }}
      aria-label="Hero Section"
    >
      {/* Header integrated into hero */}
      <header className="w-full relative z-20">
        <nav className="w-full py-4 flex flex-row items-center justify-between px-4 md:px-8 max-w-7xl mx-auto">
          {/* Logo */}
          <div
            onClick={() => {
              navigate.push("/");
            }}
            className="flex items-center space-x-3 cursor-pointer group"
          >
            <img
              src="/logo.png"
              alt="Biodata Maker Logo" 
              width={40} 
              height={40}
              className="transition-transform duration-300 group-hover:scale-110"
            />
            <span className="text-xl font-bold abc-semibold text-white">
              BiodataMaker
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Navigation Links */}
            <div className="flex items-center space-x-6">
              <a
                href="#templates"
                className="text-sm font-medium transition-colors duration-300 hover:text-pink-300 text-white/90"
              >
                Templates
              </a>
              <a
                href="#why-choose-us"
                className="text-sm font-medium transition-colors duration-300 hover:text-pink-300 text-white/90"
              >
                Features
              </a>
              <a
                href="#faq-section"
                className="text-sm font-medium transition-colors duration-300 hover:text-pink-300 text-white/90"
              >
                FAQ
              </a>
            </div>

            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 text-white/90 hover:bg-white/10"
              >
                <span>{languageMap[i18n.language] || "Language"}</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${
                    isLanguageDropdownOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Language Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <button
                    onClick={() => {
                      i18n.changeLanguage("english");
                      setIsLanguageDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    English
                  </button>
                  <button
                    onClick={() => {
                      i18n.changeLanguage("hindi");
                      setIsLanguageDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    हिंदी
                  </button>
                  <button
                    onClick={() => {
                      i18n.changeLanguage("marathi");
                      setIsLanguageDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    मराठी
                  </button>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const element = document.getElementById("create-biodata");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }
              }}
              className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-semibold rounded-full hover:from-pink-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 abc-semibold"
            >
              Create Biodata
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors duration-300 text-white hover:bg-white/10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/10 backdrop-blur-md border-t border-white/20">
            <div className="px-4 py-6 space-y-4">
              <a
                href="#templates"
                className="block text-white/90 hover:text-white transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Templates
              </a>
              <a
                href="#why-choose-us"
                className="block text-white/90 hover:text-white transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#faq-section"
                className="block text-white/90 hover:text-white transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FAQ
              </a>
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    const element = document.getElementById("create-biodata");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                  setIsMobileMenuOpen(false);
                }}
                className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-semibold rounded-full hover:from-pink-600 hover:to-purple-600 transition-all duration-300 abc-semibold"
              >
                Create Biodata
              </button>
            </div>
          </div>
        )}
      </header>
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-white rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white rounded-full blur-lg animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-white rounded-full blur-md animate-pulse" style={{animationDelay: '0.5s'}}></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-6 md:py-10">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left max-w-lg lg:max-w-none">
            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 abc-bold leading-tight animate-fade-in">
              {t("showCaseHeading")}
            </h1>
            
            {/* Subtext */}
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 md:mb-8 abc-regular leading-relaxed max-w-2xl animate-fade-in-delay">
              {t("showCaseDesc")}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start mb-4 md:mb-6 animate-fade-in-delay-2">
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    const element = document.getElementById("create-biodata");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                }}
                className="inline-flex items-center px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold text-white bg-pink-500 hover:bg-pink-600 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/25 focus:outline-none focus:ring-4 focus:ring-pink-300 abc-semibold"
                aria-label="Create Your Biodata Now"
              >
                <span className="mr-2">✨</span>
                Create Your Biodata Now
                <span className="ml-2">→</span>
              </button>
              
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    const element = document.getElementById("templates");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                }}
                className="inline-flex items-center px-5 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-white/30 abc-semibold border border-white/30"
                aria-label="View Templates"
              >
                <span className="mr-2">🎨</span>
                View Templates
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 md:gap-6 text-white/80 animate-fade-in-delay-3">
              <div className="flex items-center space-x-2">
                <div className="flex -space-x-2">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm abc-regular">4.9/5 Rating</span>
              </div>
              <div className="text-sm abc-regular">10K+ Users</div>
              <div className="text-sm abc-regular">Premium Quality</div>
            </div>
          </div>

          {/* Right Content - Template Preview */}
          <div className="relative flex justify-center lg:justify-end pb-6 md:pb-0">
            <div className="relative w-full max-w-[360px] md:max-w-none animate-float">
              {/* Main Template - Responsive */}
              <img
                src="https://d16akdef0hveqs.cloudfront.net/bgs/b3.png"
                width={350}
                height={495}
                alt="Biodata Template Preview"
                className="w-full h-auto max-w-[280px] md:max-w-[350px] mx-auto rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-500 z-10 relative object-contain"
                loading="lazy"
              />
              
              {/* Floating Badge - Hidden on mobile to prevent overlap */}
              <div className="hidden md:block absolute -top-4 -right-4 bg-white rounded-full p-3 shadow-lg animate-bounce">
                <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg font-bold">★</span>
                </div>
              </div>

              {/* Background Templates - Responsive positioning */}
              <img
                src="https://d16akdef0hveqs.cloudfront.net/bgs/b6.png"
                width={250}
                height={350}
                alt="Biodata Template Background 1"
                className="hidden md:block absolute -top-12 -left-12 w-[200px] h-auto rounded-xl shadow-2xl transform -rotate-12 opacity-85 hover:rotate-0 transition-all duration-500 animate-float-delay-1"
                loading="lazy"
              />
              
              <img
                src="https://d16akdef0hveqs.cloudfront.net/bgs/b1.png"
                width={250}
                height={350}
                alt="Biodata Template Background 2"
                className="hidden md:block absolute -bottom-12 -right-12 w-[200px] h-auto rounded-xl shadow-2xl transform rotate-12 opacity-85 hover:rotate-0 transition-all duration-500 animate-float-delay-2"
                loading="lazy"
              />

              {/* Mobile: Stacked background images */}
              <div className="md:hidden flex flex-col items-center space-y-4 mt-4">
                <img
                  src="https://d16akdef0hveqs.cloudfront.net/bgs/b6.png"
                  width={200}
                  height={280}
                  alt="Biodata Template Background 1"
                  className="w-full max-w-[200px] h-auto rounded-lg shadow-xl opacity-80 animate-fade-in-delay"
                  loading="lazy"
                />
                <img
                  src="https://d16akdef0hveqs.cloudfront.net/bgs/b1.png"
                  width={200}
                  height={280}
                  alt="Biodata Template Background 2"
                  className="w-full max-w-[200px] h-auto rounded-lg shadow-xl opacity-80 animate-fade-in-delay-2"
                  loading="lazy"
                />
              </div>

              {/* Additional floating elements - Hidden on mobile */}
              <div className="hidden md:block absolute -top-6 -left-6 w-4 h-4 bg-yellow-400 rounded-full animate-pulse opacity-60"></div>
              <div className="hidden md:block absolute -bottom-6 -right-6 w-3 h-3 bg-pink-400 rounded-full animate-pulse opacity-60" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
